import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useWaitForCeleryTaskResultsAsync } from '@/hooks/useWaitForCeleryTaskResultsAsync'
import { useCloneDataset } from '@/queries/datasetQueries'
import { showToast } from '@/theme/notifications'
import { Dataset } from '@/types/dataset'
import { FormValues } from '../DatasetForm/DatasetForm'

type CloneDatasetCallbacks = {
  onSuccess?: (dataset: Dataset) => void
}

export const useCloneDatasetHandler = () => {
  const intl = useIntl()

  const { mutateAsync: cloneDataset, isPending: isCloning } = useCloneDataset()

  const { waitForCeleryTaskResultsAsync, isPending: isPolling } =
    useWaitForCeleryTaskResultsAsync()

  const handleCloneDataset = useCallback(
    async (values: FormValues, callbacks: CloneDatasetCallbacks) => {
      const {
        originalDatasetVersion,
        includeAnnotations,
        deleteOriginal,
        name,
        description,
        labels,
        labelingType
      } = values

      if (!originalDatasetVersion) {
        return
      }

      try {
        const { task_id, dataset } = await cloneDataset({
          originalVersionId: originalDatasetVersion.id,
          data: {
            import_annotations: includeAnnotations,
            delete_original: deleteOriginal,
            name,
            description,
            labeling_type: labelingType,
            labels: labels.map((label) => ({
              name: label.name,
              color: label.color
            }))
          }
        })

        await waitForCeleryTaskResultsAsync(task_id)

        showToast(
          intl.formatMessage({ id: 'datasets.clone.success' }),
          'success'
        )

        callbacks.onSuccess?.(dataset)
      } catch (err) {
        showToast(
          extractErrorMessage(
            err,
            intl.formatMessage({ id: 'datasets.clone.error' })
          ),
          'error'
        )
      }
    },
    [cloneDataset, waitForCeleryTaskResultsAsync, intl]
  )

  const isPending = isCloning || isPolling

  return useMemo(
    () => ({
      cloneDataset: handleCloneDataset,
      isPending
    }),
    [handleCloneDataset, isPending]
  )
}
